import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';
import { useTranslation } from 'react-i18next';

import Link from '@material-ui/core/Link';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import HomeIcon from '@material-ui/icons/Home';
import { Theme } from '@material-ui/core/styles/createTheme';
import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import IconButton from '@material-ui/core/IconButton';

import NoteIcon from 'components/Icons/Note';
import Tooltip from 'components/Tooltip/Tooltip';
import LoadingIndicator from 'components/Loading/LoadingIndicator';

import { RootStore } from 'stores/rootStore';
import { BreadcrumbsStore } from 'stores/breadcrumbsStore';
import { UserStoreKC } from 'stores/userStoreKC';
import DemoModeButton from './DemoModeButton';
import { EdwardColors } from 'enums';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    home: {
      color: theme.palette.primary.main,
    },
    link: {
      color: theme.palette.text.primary,
      fontWeight: 'bold',
    },
    breadCrumb: {
      marginBottom: 5,
      flexGrow: 1,
    },
    breadCrumbs: {
      display: 'flex',
    },
    demoButton: {
      marginBottom: 10,
      marginTop: '-5px',
      textAlign: 'right',
    },
    detail: {
      '& a, & li': {
        height: theme.spacing(6),
        lineHeight: `${theme.spacing(6) + 1}px`,
        display: 'inline-flex',
        alignItems: 'center',
      },
    },
    buttons: {
      marginBottom: 10,
      marginTop: '-5px',
      textAlign: 'right',
      display: 'flex',
      '&.detailButtons': {
        '& button': {
          width: theme.spacing(6),
          height: theme.spacing(6),
        },
        marginBottom: 0,
        display: 'flex',
        alignItems: 'center',
      },
    },
    detailButtons: {
      position: 'absolute',
      right: 0,
      top: 0,
    },
  }),
);

interface Props {
  breadcrumbsStore?: BreadcrumbsStore;
  userStore?: UserStoreKC;
  rootStore?: RootStore;
}

const BreadcrumbsComponent: React.FC<Props> = ({ breadcrumbsStore, userStore, rootStore }) => {
  const classes = useStyles();
  const { breadcrumbs, home } = breadcrumbsStore!;
  const detail = rootStore?.stores.uiStore.detail;
  const { t } = useTranslation();
  const notes = rootStore!.stores.uiStore.currentAccountNotes;

  return (
    <div className={`${classes.breadCrumbs} ${detail ? classes.detail : ''}`}>
      <Breadcrumbs className={classes.breadCrumb}>
        <Link to={home} component={RouterLink} className={classes.home}>
          <HomeIcon />
        </Link>
        {breadcrumbs.map((bc) => (
          <div key={bc.pathname}>{bc.title}</div>
        ))}
      </Breadcrumbs>

      <div className={`${classes.buttons} ${detail ? 'detailButtons' : undefined}`}>
        {rootStore!.stores.uiStore.currentAccountId && (
          <Tooltip
            title={t(`${notes === null ? '' : notes ? 'accountNotes_title' : 'accountNotes_add'}`) || ''}
            placement="top"
          >
            <IconButton
              size="medium"
              onClick={notes === null ? undefined : () => rootStore!.stores.uiStore.openNotes()}
            >
              {notes === null ? (
                <LoadingIndicator />
              ) : (
                <NoteIcon
                  fontSize="small"
                  htmlColor={notes ? EdwardColors.SECONDARY : EdwardColors.GRAY_DARKER}
                  defaultFill
                  transparentBgr
                  strokeWidth={0}
                />
              )}
            </IconButton>
          </Tooltip>
        )}
        {userStore && !detail && <DemoModeButton userStore={userStore} />}
      </div>
    </div>
  );
};

export default compose(inject('breadcrumbsStore', 'userStore', 'rootStore'), observer)(BreadcrumbsComponent);
